import { nanoid } from "nanoid";
import create from "zustand";

import type { ChatMessage, ChatOutput, ChatStatus } from "./types";

type ReportingChatState = {
	messages: ChatMessage[];
	status: ChatStatus;
	streamStage: string | null;
	streamMessage: string | null;
	error: string | null;
	inputValue: string;
	// Running notes echoed back to the agent each turn (replaced on every reporting_chat_notes event).
	notes: Record<string, unknown> | null;
	// Set when this session was opened from a saved artifact — the Save affordance overwrites it
	// (Update) instead of creating a new artifact.
	loadedArtifactId: string | null;

	addUserMessage: (content: string) => string;
	startAssistantMessage: () => string;
	appendToAssistantMessage: (id: string, chunk: string) => void;
	attachOutput: (id: string, output: ChatOutput) => void;
	// Appends an already-complete assistant message without touching status, same as
	// completeAssistantMessage below — neither owns the global status; callers set it themselves,
	// scoped to whichever request currently owns the shared abort controller.
	addAssistantOutputMessage: (output: ChatOutput) => void;
	// Message-only: flips this id's isStreaming/isIncomplete and nothing else. It used to also force
	// status to "idle" unconditionally, which would stomp a replacement call's "thinking"/"streaming"
	// status if this one belonged to a superseded request. Callers now set status themselves, guarded
	// by an ownership check against the live abort controller (see useReportingChatStream.ts).
	completeAssistantMessage: (id: string, isIncomplete?: boolean) => void;
	setNotes: (notes: Record<string, unknown> | null) => void;
	setStatus: (status: ChatStatus) => void;
	setStreamStage: (stage: string | null) => void;
	setStreamMessage: (message: string | null) => void;
	setError: (error: string | null) => void;
	setInputValue: (value: string) => void;
	setLoadedArtifactId: (id: string | null) => void;
	reset: () => void;
};

const mapMessage = (messages: ChatMessage[], id: string, update: (message: ChatMessage) => ChatMessage) =>
	messages.map((message) => (message.id === id ? update(message) : message));

export const useReportingChatStore = create<ReportingChatState>((set) => ({
	messages: [],
	status: "idle",
	streamStage: null,
	streamMessage: null,
	error: null,
	inputValue: "",
	notes: null,
	loadedArtifactId: null,

	addUserMessage: (content) => {
		const id = nanoid();
		set((state) => ({ messages: [...state.messages, { id, role: "user", content }], inputValue: "" }));
		return id;
	},

	startAssistantMessage: () => {
		const id = nanoid();
		set((state) => ({ messages: [...state.messages, { id, role: "assistant", content: "", isStreaming: true }] }));
		return id;
	},

	appendToAssistantMessage: (id, chunk) =>
		set((state) => ({
			messages: mapMessage(state.messages, id, (message) => ({ ...message, content: message.content + chunk })),
		})),

	attachOutput: (id, output) =>
		set((state) => ({
			messages: mapMessage(state.messages, id, (message) => ({
				...message,
				outputs: [...(message.outputs ?? []), output],
			})),
		})),

	addAssistantOutputMessage: (output) =>
		set((state) => ({
			messages: [
				...state.messages,
				{ id: nanoid(), role: "assistant", content: "", outputs: [output], isStreaming: false },
			],
		})),

	completeAssistantMessage: (id, isIncomplete) =>
		set((state) => ({
			messages: mapMessage(state.messages, id, (message) => ({ ...message, isStreaming: false, isIncomplete })),
		})),

	setNotes: (notes) => set({ notes }),
	setStatus: (status) => set({ status }),
	setStreamStage: (streamStage) => set({ streamStage }),
	setStreamMessage: (streamMessage) => set({ streamMessage }),
	setError: (error) => set((state) => ({ error, status: error ? "error" : state.status })),
	setInputValue: (inputValue) => set({ inputValue }),
	setLoadedArtifactId: (loadedArtifactId) => set({ loadedArtifactId }),

	reset: () =>
		set({
			messages: [],
			status: "idle",
			streamStage: null,
			streamMessage: null,
			error: null,
			notes: null,
			loadedArtifactId: null,
		}),
}));
